import { useState } from "react";
import { useAtom, useAtomValue } from "jotai";

import { useArtworkForm, useClearManualEdits } from "@design-lab/entities/artwork";
import {
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pinningSessionsAtom,
  useCancelPinningSession,
  type DesignLabPlacementKey
} from "@design-lab/entities/pinning";
import { trackDesignLabPinningInstructionsRemoved } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui";
import { StarsIcon } from "@design-lab/shared/ui/icons";
import { MarkdownContent } from "@design-lab/shared/ui/MarkdownContent";

import { ConfirmActionModal } from "../ConfirmActionModal";
import { ArtistEditsInfo } from "../ArtistEditsInfo";
import { PrintPerfectExplanatoryTooltip } from "./PrintPerfectExplanatoryTooltip";

type CompletedSessionIndicatorProps = {
  placementKey: DesignLabPlacementKey;
  placementSide: "front" | "back";
};

export function CompletedSessionIndicator({
  placementKey,
  placementSide
}: CompletedSessionIndicatorProps) {
  const { form } = useArtworkForm();
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const { mutate: cancelSession, isPending: isCancellingSession } = useCancelPinningSession();
  const clearManualEdits = useClearManualEdits();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const currentArtworkId = form.watch(`${placementKey}.artworkId`) as string | undefined;
  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions,
    artworkId: currentArtworkId
  });

  const isApproved = sessionForPlacement?.state === "approved";
  const isCompleted = sessionForPlacement?.state === "completed";

  if (!sessionForPlacement || (!isCompleted && !isApproved) || !currentArtworkId) {
    return null;
  }

  const { sessionId, summary } = sessionForPlacement;

  const handleConfirmCancel = () => {
    cancelSession(
      { sessionId, artworkId: currentArtworkId },
      {
        onSuccess: () => {
          setShowConfirmModal(false);
          trackDesignLabPinningInstructionsRemoved({
            artworkId: currentArtworkId,
            sessionId
          });
          const sessionLookupKey = designProjectPlacementId || currentArtworkId;
          if (sessionLookupKey) {
            setPinningSessions((prev: typeof pinningSessions) => {
              const next = { ...prev };
              delete next[sessionLookupKey];
              return next;
            });
          }
          form.setValue(`info.${placementSide}.enhancementRequestNote` as any, "");
          clearManualEdits(placementSide);
        }
      }
    );
  };

  return (
    <div
      data-testid="completed-session-indicator"
      className={`mb-4 flex flex-col gap-3 rounded-lg p-3 ${
        isApproved ? "bg-[#AD47FF]/20" : "bg-[#E1E4FD]"
      }`}
    >
      {isApproved ? (
        <div className="flex items-center gap-2">
          <StarsIcon className="size-8" />
          <div>
            <p className="text-[15px] font-medium leading-6 text-[#0a0a0a]">AI Edit Applied</p>
            <p className="text-[11px] font-medium leading-[18px] text-gray-900">
              Your design has been updated by AI.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <StarsIcon className="size-8" />
          <div className="flex-1">
            <div className="flex justify-between gap-1.5">
              <div className="flex items-center gap-1.5">
                <p className="text-[15px] font-medium leading-6 text-[#0a0a0a]">
                  Your Artist Edits
                </p>
                <span className="rounded-full border border-white bg-[#00834f] px-1.5 py-0.5 text-xs font-medium text-white">
                  Free
                </span>
              </div>
              <div className="[&_button]:h-auto [&_button]:py-0">
                <PrintPerfectExplanatoryTooltip label="How it works" centeredMobile>
                  <ArtistEditsInfo />
                </PrintPerfectExplanatoryTooltip>
              </div>
            </div>
            <p className="text-[11px] font-medium leading-[18px] text-gray-900">
              Human artists will handle your edit after checkout.
            </p>
          </div>
        </div>
      )}
      {summary && (
        <div className="rounded-lg bg-white/70 p-3 text-xs font-medium text-gray-900">
          <MarkdownContent content={summary} />
        </div>
      )}
      <div className="flex gap-2 self-end">
        <Button
          type="button"
          data-testid="cancel-design-request-button"
          className="shrink-0 cursor-pointer rounded-lg border-[1.5px] border-[#1e2939] bg-white px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
          isDisabled={isCancellingSession}
          onPress={() => setShowConfirmModal(true)}
        >
          {isApproved ? "Undo AI Edit" : "Cancel Request"}
        </Button>
      </div>
      <ConfirmActionModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmCancel}
        isLoading={isCancellingSession}
        title={isApproved ? "Undo AI Edit?" : "Cancel Artist Request?"}
        message={
          isApproved
            ? "This will revert your design to the un-edited image. This can't be undone."
            : "Your edit request will be removed and won't be sent to our artists. You can always request it again."
        }
        confirmText={isApproved ? "Yes, revert to original" : "Yes, cancel request"}
        cancelText={isApproved ? "Keep AI edit" : "Keep request"}
        destructive
      />
    </div>
  );
}
